import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth, signInWithEmailAndPassword, onAuthStateChanged } from 'firebase/auth';
import { TrendingUp, DollarSign, Users, Calendar, Eye, Copy, CheckCircle, BarChart3, Download, Mail } from 'lucide-react';


// Main Affiliate Dashboard Component
const AffiliateDashboard = ({ appInstance }) => {
    const [affiliateData, setAffiliateData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [affiliateId, setAffiliateId] = useState(null);

    useEffect(() => {
        // Get affiliate ID from URL params or localStorage
        const urlParams = new URLSearchParams(window.location.search);
        const idFromUrl = urlParams.get('id');
        const idFromStorage = localStorage.getItem('affiliateId');
        
        const id = idFromUrl || idFromStorage;
        if (id) {
            setAffiliateId(id);
            if (idFromUrl) localStorage.setItem('affiliateId', id);
            fetchDashboardData(id);
        } else {
            setError('No affiliate ID provided');
            setLoading(false);
        }
    }, []);

    const fetchDashboardData = async (id) => {
        try {
            const response = await fetch(`https://us-central1-cuethecredits-69b39.cloudfunctions.net/getAffiliateDashboardPublic?id=${id}`);
            const data = await response.json();
            
            if (response.ok) {
                setAffiliateData(data);
            } else {
                setError(data.error || 'Affiliate not found');
            }
            setLoading(false);
        } catch (error) {
            console.error('Error fetching dashboard:', error);
            setError('Failed to load dashboard data');
            setLoading(false);
        }
    };

    if (loading) return <LoadingState />;
    if (error) return <ErrorState error={error} />;
    if (!affiliateData) return <NoDataState />;

    return (
        <div className="min-h-screen bg-gray-900 text-white">
            <Header affiliate={affiliateData.affiliate} stats={affiliateData.stats} />
            <div className="max-w-7xl mx-auto px-4 py-8">
                <StatsOverview stats={affiliateData.stats} conversions={affiliateData.conversions} />
                <div className="grid lg:grid-cols-2 gap-8 mt-8">
                    <PromoCodes affiliate={affiliateData.affiliate} />
                    <QuickStats conversions={affiliateData.conversions} />
                </div>
                <ConversionsTable conversions={affiliateData.conversions} />
            </div>
        </div>
    );
};

// Header Component
const Header = ({ affiliate, stats }) => (
    <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-6">
        <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-white">
                        Welcome back, {affiliate.name}! 👋
                    </h1>
                    <p className="text-purple-100 mt-2">
                        Your BLACKBOXcr affiliate dashboard
                    </p>
                </div>
                <div className="text-right">
                    <div className="text-2xl font-bold text-white">
                        ${stats.totalEarnings.toFixed(2)}
                    </div>
                    <div className="text-purple-100 text-sm">Total Earnings</div>
                </div>
            </div>
        </div>
    </div>
);

// Stats Overview Component
const StatsOverview = ({ stats, conversions }) => {
    // Calculate this month's earnings
    const now = new Date();
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const thisMonthEarnings = conversions
        ?.filter(c => {
            if (!c.createdAt) return false;
            const convDate = new Date(c.createdAt);
            return convDate >= firstDayOfMonth;
        })
        .reduce((sum, c) => sum + (c.commission || 0), 0) / 100 || 0;

    const statCards = [
        {
            title: 'Total Conversions',
            value: stats.totalConversions || 0,
            icon: Users,
            color: 'from-blue-500 to-blue-600',
            description: 'Customers referred'
        },
        {
            title: 'Total Earnings',
            value: `$${stats.totalEarnings.toFixed(2)}`,
            icon: DollarSign,
            color: 'from-green-500 to-green-600',
            description: 'Commission earned'
        },
        {
            title: 'Pending Payouts',
            value: stats.pendingPayouts || 0,
            icon: TrendingUp,
            color: 'from-purple-500 to-purple-600',
            description: 'Awaiting payment'
        },
        {
            title: 'This Month',
            value: `$${thisMonthEarnings.toFixed(2)}`,
            icon: Calendar,
            color: 'from-orange-500 to-orange-600',
            description: 'Current month earnings'
        }
    ];

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {statCards.map((stat, index) => (
                <div key={index} className="bg-gray-800 rounded-xl p-6 border border-gray-700">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-400 text-sm font-medium">{stat.title}</p>
                            <p className="text-2xl font-bold text-white mt-1">{stat.value}</p>
                            <p className="text-gray-500 text-xs mt-1">{stat.description}</p>
                        </div>
                        <div className={`p-3 rounded-lg bg-gradient-to-r ${stat.color}`}>
                            <stat.icon className="w-6 h-6 text-white" />
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

// Promo Codes Component
const PromoCodes = ({ affiliate }) => {
    const [copied, setCopied] = useState(null);

    const copyToClipboard = (code) => {
        navigator.clipboard.writeText(code);
        setCopied(code);
        setTimeout(() => setCopied(null), 2000);
    };

    return (
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <h3 className="text-xl font-bold text-white mb-6 flex items-center">
                <Eye className="w-5 h-5 mr-2" />
                Your Promo Codes
            </h3>
            
            <div className="space-y-4">
                {affiliate.promoCodes?.map((code, index) => (
                    <div key={index} className="bg-gray-700 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="font-mono text-lg font-bold text-purple-400">
                                    {code}
                                </div>
                                <div className="text-gray-400 text-sm">
                                    Customer gets discount • You earn {(affiliate.commissionRate * 100).toFixed(0)}% commission
                                </div>
                            </div>
                            <button
                                onClick={() => copyToClipboard(code)}
                                className="flex items-center space-x-2 bg-purple-600 hover:bg-purple-700 px-3 py-2 rounded-lg transition-colors"
                            >
                                {copied === code ? (
                                    <CheckCircle className="w-4 h-4" />
                                ) : (
                                    <Copy className="w-4 h-4" />
                                )}
                                <span className="text-sm">
                                    {copied === code ? 'Copied!' : 'Copy'}
                                </span>
                            </button>
                        </div>
                    </div>
                )) || (
                    <div className="text-gray-400 text-center py-8">
                        No promo codes assigned yet
                    </div>
                )}
            </div>
        </div>
    );
};

// Quick Stats Component
const QuickStats = ({ conversions }) => {
    const avgCommission = conversions?.length > 0
        ? conversions.reduce((sum, c) => sum + (c.commission || 0), 0) / conversions.length / 100
        : 0;

    const lastConversion = conversions?.[0]?.createdAt 
        ? new Date(conversions[0].createdAt).toLocaleDateString()
        : 'No conversions yet';

    return (
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <h3 className="text-xl font-bold text-white mb-6 flex items-center">
                <BarChart3 className="w-5 h-5 mr-2" />
                Performance Metrics
            </h3>
            
            <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-gray-700">
                    <span className="text-gray-400">Average Commission</span>
                    <span className="text-white font-semibold">${avgCommission.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-700">
                    <span className="text-gray-400">Total Conversions</span>
                    <span className="text-white font-semibold">{conversions?.length || 0}</span>
                </div>
                <div className="flex justify-between items-center py-3">
                    <span className="text-gray-400">Last Conversion</span>
                    <span className="text-white font-semibold">{lastConversion}</span>
                </div>
            </div>
        </div>
    );
};

// Conversions Table Component - FIXED COLUMN MAPPING
const ConversionsTable = ({ conversions }) => {
    // Sort conversions by date (newest first)
    const sortedConversions = conversions?.sort((a, b) => {
        if (!a.createdAt) return 1;
        if (!b.createdAt) return -1;
        return new Date(b.createdAt) - new Date(a.createdAt);
    }) || [];

    return (
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 mt-8">
            <h3 className="text-xl font-bold text-white mb-6 flex items-center">
                <TrendingUp className="w-5 h-5 mr-2" />
                Recent Conversions
            </h3>
            
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead>
                        <tr className="text-gray-400 text-sm">
                            <th className="py-3 px-4 text-left">Date</th>
                            <th className="py-3 px-4 text-left">Plan</th>
                            <th className="py-3 px-4 text-left">Code Used</th>
                            <th className="py-3 px-4 text-left">Customer</th>
                            <th className="py-3 px-4 text-left">Payment ID</th>
                            <th className="py-3 px-4 text-left">Sale Amount</th>
                            <th className="py-3 px-4 text-left">Commission</th>
                            <th className="py-3 px-4 text-left">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedConversions.length > 0 ? (
                            sortedConversions.map((conversion, index) => (
                                <tr key={conversion.id || index} className="border-b border-gray-700 hover:bg-gray-750">
                                    <td className="py-3 px-4 text-gray-300">
                                        {conversion.createdAt ? new Date(conversion.createdAt).toLocaleDateString() : 'Unknown'}
                                    </td>
                                    <td className="py-3 px-4 text-gray-300 capitalize">
                                        {conversion.plan || 'Unknown'}
                                    </td>
                                    <td className="py-3 px-4 text-purple-400 font-mono text-sm">
                                        {conversion.promoCode || 'N/A'}
                                    </td>
                                    <td className="py-3 px-4 text-gray-300 text-sm">
                                        {conversion.customerEmail || 'Unknown'}
                                    </td>
                                    <td className="py-3 px-4 text-gray-400 font-mono text-xs">
                                        {conversion.paymentIntentId 
                                            ? conversion.paymentIntentId.substring(0, 12) + '...' 
                                            : 'N/A'}
                                    </td>
                                    <td className="py-3 px-4 text-gray-300">
                                        ${(conversion.finalAmount / 100).toFixed(2)}
                                    </td>
                                    <td className="py-3 px-4 text-green-400 font-semibold">
                                        ${conversion.commission && !isNaN(conversion.commission) 
                                            ? (conversion.commission / 100).toFixed(2) 
                                            : '0.00'}
                                    </td>
                                    <td className="py-3 px-4">
                                        <StatusBadge status={conversion.status} paidOut={conversion.paidOut} />
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="8" className="text-center py-8 text-gray-400">
                                    No conversions yet. Start promoting to see results here!
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {sortedConversions.length > 0 && (
                <div className="mt-4 text-sm text-gray-400 text-center">
                    Showing {sortedConversions.length} conversion{sortedConversions.length !== 1 ? 's' : ''}
                </div>
            )}
        </div>
    );
};

// Status Badge Component - Enhanced
const StatusBadge = ({ status, paidOut }) => {
    // Determine display status based on payment status and paidOut flag
    let displayStatus, colorClass;
    
    if (paidOut) {
        displayStatus = 'Paid Out';
        colorClass = 'bg-blue-600 text-white';
    } else if (status === 'paid') {
        displayStatus = 'Pending Payout';
        colorClass = 'bg-yellow-600 text-white';
    } else if (status === 'pending_payment') {
        displayStatus = 'Processing';
        colorClass = 'bg-orange-600 text-white';
    } else if (status === 'free_conversion') {
        displayStatus = 'Free Trial';
        colorClass = 'bg-gray-600 text-white';
    } else {
        displayStatus = 'Unknown';
        colorClass = 'bg-gray-700 text-gray-300';
    }

    return (
        <span className={`${colorClass} px-2 py-1 rounded text-xs font-medium`}>
            {displayStatus}
        </span>
    );
};

// Loading State
const LoadingState = () => (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
            <p className="text-gray-400">Loading your dashboard...</p>
        </div>
    </div>
);

// Error State
const ErrorState = ({ error }) => (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
            <p className="text-red-400 mb-4">{error}</p>
            <button 
                onClick={() => window.location.reload()} 
                className="bg-purple-600 hover:bg-purple-700 px-4 py-2 rounded-lg"
            >
                Try Again
            </button>
        </div>
    </div>
);

// No Data State
const NoDataState = () => (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
            <p className="text-gray-400">No affiliate data found</p>
        </div>
    </div>
);

export default AffiliateDashboard;